import * as THREE from 'three';
import EventEmitter from 'eventemitter3';
import { PanelSurface, type PanelRayHit, type PanelRegion, type PointerRay } from '@/xr/panelRay';
import { colors } from '@/utils/colors';

/**
 * One row. The shape covers all three kinds of row the menu needs without a
 * discriminated union, because they differ only in what is drawn on the right:
 *
 *  - plain action — `{ id, label }`, whole row emits `id`
 *  - value row    — add `value`, which draws on the right; still emits `id`,
 *                   so a setting that cycles on each press works unchanged
 *  - two-way      — add `prevId`, which puts ◀ ▶ around the value; ◀ emits
 *                   `prevId` and ▶ emits `id`
 *
 * Every id is emitted verbatim, so a menu can be wired to an existing handler
 * without that handler learning anything about this panel.
 */
export type ActionMenuItem = {
  /** Emitted on press — or on the ▶ side of a two-way row. */
  id: string;
  label: () => string;
  /** Right-hand value text. Re-read on every redraw so it tracks live state. */
  value?: () => string;
  /** Present ⇒ the row gets ◀ ▶ arrows, and ◀ emits this id. */
  prevId?: string;
  /** Greys the row out and stops it emitting. */
  disabled?: () => boolean;
};

export type ActionMenuSection = {
  id: string;
  /** Nav label down the left edge. */
  title: string;
  items: ActionMenuItem[];
};

export type ActionMenuOptions = {
  sections: ActionMenuSection[];
  /**
   * Global actions for the right-hand rail — the ones that belong to the
   * session rather than to any one section (help, reload, exit). Kept off the
   * nav so switching sections never hides the way out.
   */
  rail?: ActionMenuItem[];
  /** Title-bar text. */
  title?: string;
  /** Panel width in metres. */
  widthMeters?: number;
  /** Distance in front of the head when opened, metres. */
  distance?: number;
  /** Status chip in the title bar (connection state, mode). */
  status?: () => { text: string; tone: 'ok' | 'warn' | 'bad' } | undefined;
  /**
   * Footer line. Defaults to the control hint, which is worth the space: the
   * bindings that open and drive this panel are invisible once you're inside a
   * session, and the rows never grow far enough down to fill the panel anyway.
   */
  hint?: string;
};

interface EventMap {
  openChanged: (open: boolean) => void;
  select: (id: string) => void;
}

// 16:9, like the menu this is modelled on — a wide panel puts the nav, the
// content and the rail side by side instead of stacking them down a column.
const CANVAS_W = 1280;
const CANVAS_H = 720;

const PAD = 32;
const TITLE_H = 88;
const NAV_W = 296;
const NAV_H = 78;
const NAV_GAP = 12;
const RAIL_W = 150;
const RAIL_H = 82;
const RAIL_GAP = 12;
const ROW_H = 76;
const ROW_GAP = 14;
const ARROW_W = 84;

const CONTENT_X = PAD + NAV_W + 28;
const CONTENT_W = CANVAS_W - CONTENT_X - RAIL_W - PAD * 2;
const RAIL_X = CANVAS_W - PAD - RAIL_W;
const BODY_Y = TITLE_H + 22;

/** Parked and forgotten is worse for a panel this size than for the wrist one. */
const IDLE_CLOSE_S = 20;

const TONES: Record<'ok' | 'warn' | 'bad', string> = {
  ok: colors.primary ?? '#21d48d',
  warn: colors.yellow ?? 'rgb(255, 202, 68)',
  bad: colors.red ?? '#ee3333',
};

const PANEL_FILL = 'rgba(14, 24, 25, 0.95)';
const TITLE_FILL = 'rgba(33, 212, 141, 0.10)';
const ROW_IDLE = 'rgba(255,255,255,0.20)';
const DIM_TEXT = 'rgba(255,255,255,0.34)';

/**
 * The full-size in-session menu, opened with B in controller mode and driven by
 * the controller ray.
 *
 * It exists because the wrist menu can't be both things. That panel is sized to
 * sit on a wrist and be poked with a fingertip, which caps how many options it
 * can hold — the range already resorted to making two of its eight slots do
 * double duty. With a controller in hand there is no wrist to wear a panel and
 * no free finger to poke it, so the constraint buys nothing.
 *
 * The layout follows the shape VR golf players already know: a title bar, a nav
 * column of sections on the left, the selected section's rows in the middle, and
 * a rail of session-wide actions pinned to the right edge so the way out is
 * never behind a section change.
 *
 * Parked rather than head-following, for the reason `InfoPanel` parks: resting a
 * laser on a target that eases toward your gaze every frame is impossible.
 */
export class ActionMenu extends EventEmitter<EventMap> {
  object: THREE.Mesh;
  isOpen = false;
  /** Index of the visible section — the nav column's selected entry. */
  section = 0;

  #sections: ActionMenuSection[];
  #rail: ActionMenuItem[];
  #title: string;
  #hint: string;
  #status?: ActionMenuOptions['status'];
  #distance: number;
  #width: number;
  #height: number;

  #surface = new PanelSurface();
  #canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #texture: THREE.CanvasTexture;
  #cacheKey = '';
  #lastHitAt = 0;

  // scratch
  #headFwd = new THREE.Vector3();

  constructor(options: ActionMenuOptions) {
    super();
    this.#sections = options.sections;
    this.#rail = options.rail ?? [];
    this.#title = options.title ?? 'Menu';
    this.#hint = options.hint ?? 'Point with the controller · trigger selects · B closes';
    this.#status = options.status;
    this.#distance = options.distance ?? 0.95;
    this.#width = options.widthMeters ?? 0.78;
    this.#height = this.#width * (CANVAS_H / CANVAS_W);

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = CANVAS_H;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for action menu');
    this.#ctx = ctx;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4; // read at an angle when parked off-centre

    this.object = new THREE.Mesh(
      new THREE.PlaneGeometry(this.#width, this.#height),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false, // always readable, never clipped by the world
        fog: false,
        side: THREE.DoubleSide,
      })
    );
    this.object.name = 'ActionMenu';
    this.object.renderOrder = 40; // with the wrist menu, over everything else
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;

    this.#buildRegions();
    this.#draw();
  }

  /**
   * Rebuilt whenever the section changes, since row counts differ. Ids are the
   * caller's own; `regionAt` is a whitelist, so any pixel that isn't part of a
   * control can never be clicked.
   */
  #buildRegions() {
    const regions: PanelRegion[] = [];
    const rect = (id: string, x: number, y: number, w: number, h: number) =>
      regions.push({ id, x: x / CANVAS_W, y: y / CANVAS_H, w: w / CANVAS_W, h: h / CANVAS_H });

    rect('close', CANVAS_W - PAD - 64, (TITLE_H - 52) / 2, 64, 52);

    this.#sections.forEach((section, index) => {
      rect(`tab:${section.id}`, PAD, BODY_Y + index * (NAV_H + NAV_GAP), NAV_W, NAV_H);
    });

    let y = BODY_Y;
    for (const item of this.#sections[this.section]?.items ?? []) {
      if (item.prevId) {
        // ◀ [label · value] ▶ — the arrows own the ends and the row body is
        // inert, so a ray landing between them does nothing rather than
        // silently cycling forward
        rect(item.prevId, CONTENT_X, y, ARROW_W, ROW_H);
        rect(item.id, CONTENT_X + CONTENT_W - ARROW_W, y, ARROW_W, ROW_H);
      } else {
        rect(item.id, CONTENT_X, y, CONTENT_W, ROW_H);
      }
      y += ROW_H + ROW_GAP;
    }

    this.#rail.forEach((item, index) => {
      rect(item.id, RAIL_X, BODY_Y + index * (RAIL_H + RAIL_GAP), RAIL_W, RAIL_H);
    });

    this.#surface.regions = regions;
  }

  /** Snap in front of the head and show. Re-opening returns to the first tab. */
  open(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }) {
    this.section = 0;
    this.#buildRegions();
    this.#headFwd.set(0, 0, -1).applyQuaternion(head.quaternion);
    this.object.position.copy(head.position).addScaledVector(this.#headFwd, this.#distance);
    // Yaw-only facing so the labels stay upright whatever the head is doing
    this.object.rotation.set(0, Math.atan2(this.#headFwd.x, this.#headFwd.z) + Math.PI, 0);
    this.#lastHitAt = this.#surface.elapsed;
    this.#setOpen(true);
  }

  close() {
    this.#setOpen(false);
  }

  toggle(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }) {
    if (this.isOpen) this.close();
    else this.open(head);
  }

  /** Session end: drop transient state. */
  reset() {
    this.#setOpen(false);
    this.section = 0;
    this.#surface.reset();
  }

  /**
   * Hover handling for controller rays. Returns the ray that landed so the
   * caller can rest its pointer dot on the surface; `activateHover` is what
   * actually clicks. Call every frame, open or not — the idle timer runs off
   * this clock.
   */
  updateRay(dt: number, rays: readonly PointerRay[]): PanelRayHit | undefined {
    if (!this.isOpen) {
      this.#surface.aim(dt, [], this.object, this.#width, this.#height);
      return undefined;
    }

    const hit = this.#surface.aim(dt, rays, this.object, this.#width, this.#height);
    if (hit) {
      this.#lastHitAt = this.#surface.elapsed;
    } else if (this.#surface.elapsed - this.#lastHitAt > IDLE_CLOSE_S) {
      // Forgotten, floating in the room — don't leave it there
      this.close();
      return undefined;
    }
    this.#draw(); // the cache key makes this a no-op unless something changed
    return hit;
  }

  /** Click whatever the ray is hovering (wired to the controller trigger). */
  activateHover(): boolean {
    if (!this.isOpen) return false;
    const id = this.#surface.press();
    if (!id) return false;
    this.#lastHitAt = this.#surface.elapsed;
    this.#draw();

    if (id === 'close') {
      this.close();
      return true;
    }
    if (id.startsWith('tab:')) {
      const index = this.#sections.findIndex(s => `tab:${s.id}` === id);
      if (index >= 0 && index !== this.section) {
        this.section = index;
        this.#buildRegions();
        this.#draw();
      }
      return true;
    }

    // A disabled row swallows the click rather than emitting — the press flash
    // still fires, so it reads as "pressed, nothing happened", not a dead panel
    const item = [...(this.#sections[this.section]?.items ?? []), ...this.#rail]
      .find(i => i.id === id || i.prevId === id);
    if (item?.disabled?.()) return true;

    this.emit('select', id);
    return true;
  }

  /** Desktop `?sim=1` layout preview — parks the panel in front of a camera. */
  forceOpenAt(camera: THREE.Camera) {
    if (this.isOpen) {
      this.close();
      return;
    }
    this.#headFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    this.object.position.copy(camera.position).addScaledVector(this.#headFwd, this.#distance);
    this.object.quaternion.copy(camera.quaternion);
    this.#lastHitAt = this.#surface.elapsed;
    this.#setOpen(true);
  }

  #setOpen(open: boolean) {
    if (open === this.isOpen) return;
    this.isOpen = open;
    this.object.visible = open;
    this.#surface.reset();
    if (open) this.#draw();
    this.emit('openChanged', open);
  }

  #draw() {
    const section = this.#sections[this.section];
    if (!section) return;
    const status = this.#status?.();

    const signature = (items: ActionMenuItem[]) =>
      items.map(i => `${i.label()}=${i.value?.() ?? ''}${i.disabled?.() ? '!' : ''}`).join(',');
    const key = `${this.section}|${this.#surface.hover}|${this.#surface.pressed}`
      + `|${status?.text ?? ''}|${status?.tone ?? ''}`
      + `|${signature(section.items)}|${signature(this.#rail)}`;
    if (key === this.#cacheKey) return;
    this.#cacheKey = key;

    const ctx = this.#ctx;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.fillStyle = PANEL_FILL;
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_W, CANVAS_H, 30);
    ctx.fill();
    ctx.strokeStyle = 'rgba(33, 212, 141, 0.4)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textBaseline = 'middle';

    this.#drawTitleBar(section, status);
    this.#drawNav();
    this.#drawRows(section);
    this.#drawRail();

    if (this.#hint) {
      ctx.textAlign = 'left';
      ctx.font = '25px Rubik, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.38)';
      ctx.fillText(this.#hint, PAD, CANVAS_H - PAD - 6, CANVAS_W - PAD * 2 - RAIL_W);
    }

    this.#texture.needsUpdate = true;
  }

  /** Rounded rect in the shared three-state style: pressed, hovered, idle. */
  #frame(region: PanelRegion, radius = 16): { x: number; y: number; w: number; h: number; lit: boolean } {
    const ctx = this.#ctx;
    const x = region.x * CANVAS_W;
    const y = region.y * CANVAS_H;
    const w = region.w * CANVAS_W;
    const h = region.h * CANVAS_H;
    const lit = this.#surface.pressed === region.id;

    ctx.beginPath();
    ctx.roundRect(x, y, w, h, radius);
    if (lit) {
      ctx.fillStyle = colors.primary;
      ctx.fill();
    } else if (this.#surface.hover === region.id) {
      ctx.fillStyle = 'rgba(33, 212, 141, 0.14)';
      ctx.fill();
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 4;
      ctx.stroke();
    } else {
      ctx.strokeStyle = ROW_IDLE;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    return { x, y, w, h, lit };
  }

  #region(id: string): PanelRegion | undefined {
    return this.#surface.regions.find(r => r.id === id);
  }

  #drawTitleBar(section: ActionMenuSection, status?: { text: string; tone: 'ok' | 'warn' | 'bad' }) {
    const ctx = this.#ctx;

    ctx.beginPath();
    // [tl, tr, br, bl] — square at the bottom so it meets the body cleanly
    ctx.roundRect(2, 2, CANVAS_W - 4, TITLE_H, [28, 28, 0, 0]);
    ctx.fillStyle = TITLE_FILL;
    ctx.fill();

    ctx.textAlign = 'left';
    ctx.font = 'bold 42px Rubik, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(this.#title.toUpperCase(), PAD, TITLE_H / 2, 420);

    // Section name trails the title, the way a breadcrumb does — the nav shows
    // where you are too, but only the title bar is where the eye starts
    const titleW = ctx.measureText(this.#title.toUpperCase()).width;
    ctx.font = '30px Rubik, sans-serif';
    ctx.fillStyle = colors.primary;
    ctx.fillText(`· ${section.title}`, PAD + Math.min(titleW, 420) + 18, TITLE_H / 2 + 2, 300);

    if (status) {
      // Right-aligned, clear of the close button
      const right = CANVAS_W - PAD - 96;
      ctx.font = '27px Rubik, sans-serif';
      const width = Math.min(ctx.measureText(status.text).width, 420);
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText(status.text, right, TITLE_H / 2, 420);
      ctx.beginPath();
      ctx.arc(right - width - 20, TITLE_H / 2, 8, 0, Math.PI * 2);
      ctx.fillStyle = TONES[status.tone];
      ctx.fill();
    }

    const close = this.#region('close');
    if (close) {
      const box = this.#frame(close, 14);
      ctx.textAlign = 'center';
      ctx.font = 'bold 36px Rubik, sans-serif';
      ctx.fillStyle = box.lit ? '#111c1c' : '#ffffff';
      ctx.fillText('×', box.x + box.w / 2, box.y + box.h / 2 - 2);
    }
  }

  #drawNav() {
    const ctx = this.#ctx;
    ctx.textAlign = 'left';
    this.#sections.forEach((section, index) => {
      const region = this.#region(`tab:${section.id}`);
      if (!region) return;
      const selected = index === this.section;
      const x = region.x * CANVAS_W;
      const y = region.y * CANVAS_H;
      const w = region.w * CANVAS_W;
      const h = region.h * CANVAS_H;

      if (selected) {
        // The selected entry is filled, not outlined — it reads as the current
        // place rather than as another thing to press
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 16);
        ctx.fillStyle = colors.primary;
        ctx.fill();
      } else {
        this.#frame(region);
      }

      ctx.font = 'bold 33px Rubik, sans-serif';
      ctx.fillStyle = selected || this.#surface.pressed === region.id ? '#0e1819' : '#ffffff';
      ctx.fillText(section.title, x + 26, y + h / 2, w - 44);
    });
  }

  #drawRows(section: ActionMenuSection) {
    const ctx = this.#ctx;

    for (const item of section.items) {
      const main = this.#region(item.id);
      if (!main) continue;
      const dimmed = !!item.disabled?.();

      if (item.prevId) {
        const rowY = main.y * CANVAS_H;
        ctx.beginPath();
        ctx.roundRect(CONTENT_X, rowY, CONTENT_W, ROW_H, 16);
        ctx.strokeStyle = ROW_IDLE;
        ctx.lineWidth = 2;
        ctx.stroke();

        const prev = this.#region(item.prevId);
        if (prev) this.#glyph(this.#frame(prev), '◀', 36);
        this.#glyph(this.#frame(main), '▶', 36);

        ctx.textAlign = 'left';
        ctx.font = 'bold 31px Rubik, sans-serif';
        ctx.fillStyle = dimmed ? DIM_TEXT : '#ffffff';
        ctx.fillText(item.label(), CONTENT_X + ARROW_W + 24, rowY + ROW_H / 2, CONTENT_W * 0.4);
        ctx.textAlign = 'right';
        ctx.font = 'bold 33px Rubik, sans-serif';
        ctx.fillStyle = dimmed ? DIM_TEXT : colors.primary;
        ctx.fillText(item.value?.() ?? '', CONTENT_X + CONTENT_W - ARROW_W - 24, rowY + ROW_H / 2, CONTENT_W * 0.42);
        continue;
      }

      const box = this.#frame(main);
      const value = item.value?.();
      ctx.textAlign = 'left';
      ctx.font = 'bold 31px Rubik, sans-serif';
      ctx.fillStyle = box.lit ? '#0e1819' : dimmed ? DIM_TEXT : '#ffffff';
      ctx.fillText(item.label(), box.x + 26, box.y + box.h / 2, box.w - (value ? 280 : 52));
      if (value) {
        ctx.textAlign = 'right';
        ctx.font = 'bold 33px Rubik, sans-serif';
        ctx.fillStyle = box.lit ? '#0e1819' : dimmed ? DIM_TEXT : colors.primary;
        ctx.fillText(value, box.x + box.w - 26, box.y + box.h / 2, 250);
      }
    }
  }

  /**
   * The right-hand rail. Text, not icons: a glyph set that renders on the dev
   * machine is no guarantee on the headset's browser, and a tofu box in the
   * corner of a menu is worse than a word.
   */
  #drawRail() {
    const ctx = this.#ctx;
    ctx.beginPath();
    ctx.moveTo(RAIL_X - 22, BODY_Y - 4);
    ctx.lineTo(RAIL_X - 22, CANVAS_H - PAD);
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 2;
    ctx.stroke();

    for (const item of this.#rail) {
      const region = this.#region(item.id);
      if (!region) continue;
      const box = this.#frame(region, 16);
      const dimmed = !!item.disabled?.();
      ctx.textAlign = 'center';
      ctx.font = 'bold 28px Rubik, sans-serif';
      ctx.fillStyle = box.lit ? '#0e1819' : dimmed ? DIM_TEXT : '#ffffff';
      ctx.fillText(item.label(), box.x + box.w / 2, box.y + box.h / 2, box.w - 18);
    }
  }

  #glyph(box: { x: number; y: number; w: number; h: number; lit: boolean }, text: string, size: number) {
    const ctx = this.#ctx;
    ctx.textAlign = 'center';
    ctx.font = `bold ${size}px Rubik, sans-serif`;
    ctx.fillStyle = box.lit ? '#0e1819' : '#ffffff';
    ctx.fillText(text, box.x + box.w / 2, box.y + box.h / 2, box.w - 12);
  }
}
